/**
 * Consolidated secured route handler
 */
import React, { useEffect, useRef } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { get } from "lodash";
import { TokenManager } from "@smfa/utils";
import { logout as storeLogout } from "@smfa/utils/src/store";
import { logoutUser as storeLogoutUser } from "@smfa/utils/src/store";

/**
 * Route securing component
 * @param {string} [props.loginRoute] - The route to redirect to if not authenticated
 * @param {string} [props.successRoute] - The route to redirect to if authenticated
 * @param {string} [props.failureRoute] - The route to redirect to if authentication fails
 * @param {string} [props.requiredRole=""] - The role required to access the route - Default allows any authenticated role.
 * @returns {JSX.Element}
 * @component
 */
const SecuredRoute = ({
  loginRoute = "/login",
  successRoute = "",
  failureRoute = "",
  requiredRole = "",
}) => {
  const dispatch = useDispatch();
  const hasCleared = useRef(false);

  const auth = useSelector((state) => state.auth);
  const role = useSelector((state) => get(state, "user.data.role", null));

  const isAuthenticated = Boolean(auth.isAuthenticated);
  const hasToken = Boolean(TokenManager.getToken());

  // Safety net: if Redux says authenticated but the token cookie is missing,
  // the session was invalidated (e.g. server-side token revocation cleared the
  // cookie via the axios 401 interceptor or initializeSession). Dispatch
  // logout so the persisted Redux state doesn't keep the user "stuck".
  useEffect(() => {
    if (isAuthenticated && !hasToken && !hasCleared.current) {
      hasCleared.current = true;
      console.warn(
        "[utils] SecuredRoute: Redux says authenticated but no token cookie found - forcing logout",
      );
      dispatch(storeLogout());
      dispatch(storeLogoutUser());
    } else if (hasToken) {
      // Reset so the safety net can fire again if the token is removed later
      hasCleared.current = false;
    }
  }, [isAuthenticated, hasToken, dispatch]);

  if (isAuthenticated && !hasToken) {
    // Cookie is gone; redirect immediately while the useEffect cleans up Redux
    return <Navigate to={failureRoute || loginRoute} />;
  }

  if (!isAuthenticated) {
    return <Navigate to={failureRoute || loginRoute} />;
  } else if (requiredRole && role !== requiredRole) {
    // Role is missing or does not match the required role.
    // Do NOT clear the token -- it may be valid for the other app
    // sharing this cookie (client and admin share localhost cookies).
    return <Navigate to={loginRoute} />;
  } else {
    return <Outlet />;
  }
};

export default SecuredRoute;
